import { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";

export interface TreatmentFormData {
  id?: string;
  name: string;
  price: number;
  duration: number;
  status: "active" | "inactive";
  description?: string;
}

interface TreatmentModalProps {
  isOpen: boolean;
  mode: "add" | "edit";
  treatment?: TreatmentFormData | null;
  onClose: () => void;
  onSave: (data: TreatmentFormData) => void;
  saving?: boolean;
}

export const TreatmentModal = ({
  isOpen,
  mode,
  treatment,
  onClose,
  onSave,
  saving = false,
}: TreatmentModalProps) => {
  const [formData, setFormData] = useState<TreatmentFormData>({
    name: "",
    price: 0,
    duration: 30,
    status: "active",
    description: "",
  });

  useEffect(() => {
    if (isOpen) {
      if (treatment) {
        setFormData({
          id: treatment.id,
          name: treatment.name,
          price: treatment.price,
          duration: treatment.duration,
          status: treatment.status,
          description: treatment.description || "",
        });
      } else {
        setFormData({
          name: "",
          price: 0,
          duration: 30,
          status: "active",
          description: "",
        });
      }
    }
  }, [isOpen, treatment]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSave(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-lg p-0 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === "add" ? "Add New Treatment" : "Edit Treatment"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
          {/* Treatment Name */}
          <div>
            <label className="text-sm font-semibold text-gray-700">
              Treatment Name<span className="text-red-500">*</span>
            </label>
            <input
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Botox Treatment"
              className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
            />
          </div>

          {/* Price & Duration Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Price (£)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.price || ""}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setFormData((prev) => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">
                Duration (minutes)
              </label>
              <input
                type="number"
                min="1"
                value={formData.duration || ""}
                onFocus={(e) => e.target.select()}
                onChange={(e) => setFormData((prev) => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB]"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-sm font-semibold text-gray-700">Status</label>
            <div className="mt-2 flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  checked={formData.status === "active"}
                  onChange={() => setFormData((prev) => ({ ...prev, status: "active" }))}
                  className="w-4 h-4 text-[#885ABB] focus:ring-[#885ABB]"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  checked={formData.status === "inactive"}
                  onChange={() => setFormData((prev) => ({ ...prev, status: "inactive" }))}
                  className="w-4 h-4 text-[#885ABB] focus:ring-[#885ABB]"
                />
                <span className="text-sm text-gray-700">Inactive</span>
              </label>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-semibold text-gray-700">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              placeholder="Brief description of the treatment..."
              className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-[#885ABB] resize-none"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.name.trim() || saving}
              className="px-6 py-2 rounded-lg bg-[#885ABB] hover:bg-[#6f47a2] text-white transition-colors disabled:opacity-50 flex items-center gap-2 font-medium"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>{mode === "add" ? "Add Treatment" : "Save Changes"}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
